import React, {useState} from "react";
import {useWatch} from "react-hook-form";
import {SelectedComp} from "./configurableCompsSelectTypes.tsx";
import Modal from "../../../Modal.tsx";
import {Circle, Check, ChevronLeft, ChevronRight, Loader} from "lucide-react";
import {useFormMethods} from "../../DyanmicFormContext.tsx";
//because we check in the thing that renders this, we know property valuation is not undefined!


interface CompCaImageModalProps {
    clickedCompImage?: SelectedComp;
    imageListOverride?: string[];
    titleOverride?: string;
    changeCompImage: (comp: SelectedComp, newImageUrl: string) => void;
    setClickSelectableComp: (comp: SelectedComp | null) => void;
    onClose: () => void;
}

const CompCaImageModalRenderer: React.FC<CompCaImageModalProps> = ({
                                                                       clickedCompImage, imageListOverride, titleOverride, changeCompImage, setClickSelectableComp, onClose
                                                                                             }) => {
    const { formMethods } = useFormMethods()
    const [isLoading, setIsLoading] = useState(true);

    if (!clickedCompImage && !imageListOverride) return;

    const imageList: string[] = imageListOverride ?? formMethods.getValues(`${clickedCompImage!.dgInfo.dataGridFieldKey}.${clickedCompImage!.indexOfDg}.${clickedCompImage!.dgInfo.prefixForApiPopulation}AllImages`);

    const [carouselIndex, setCarouselIndex] = useState(0);
    const itemsPerPage = 6;
    const totalImages = imageList?.length || 0;
    const maxIndex = Math.max(0, totalImages - itemsPerPage);

    // Watch these keys so the checkmark reacts to form updates
    const compImageUrlKey = clickedCompImage
        ? `${clickedCompImage.dgInfo.dataGridFieldKey}.${clickedCompImage.indexOfDg}.${clickedCompImage.dgInfo.prefixForApiPopulation}ImageUrl`
        : "";

    // Used to detect when a custom uploaded image is overriding the CA image selection
    const prosConsKey = clickedCompImage
        ? `${clickedCompImage.dgInfo.dataGridFieldKey}.${clickedCompImage.indexOfDg}.${clickedCompImage.dgInfo.prefixForApiPopulation}ProsCons`
        : "";

    // Current saved comp image URl from the form
    const compImageUrl = useWatch({
        control: formMethods.control,
        name: compImageUrlKey
    });

    const prosConsValue = useWatch({
        control: formMethods.control,
        name: prosConsKey,
        defaultValue: []
    });

    // If a custom ProsCons image exists and ImageUrl is empty, show no checkmarks
    const hasProsConsImage = Array.isArray(prosConsValue) && prosConsValue.length > 0;
    const isCustomOverrideActive = hasProsConsImage && !compImageUrl;

    // Currently previewed image in the modal
    const [currentImage, setCurrentImage] = useState(
        clickedCompImage
            ? (compImageUrl && imageList?.includes(compImageUrl) ? compImageUrl : imageList?.[0])
            : imageList?.[0]
    );

    // Checked image that is the actual image for the comp
    const [selectedCompImageUrl, setSelectedCompImageUrl] = useState(
        clickedCompImage
            ? (isCustomOverrideActive ? undefined : (compImageUrl && imageList?.includes(compImageUrl) ? compImageUrl : imageList?.[0]))
            : undefined
    );

    const isSelectedForComp = (imageUrl: string): boolean => {
        return !!clickedCompImage && imageUrl === selectedCompImageUrl;
    };

    const handlePrevious = () => {
        setCarouselIndex((prev: number) => Math.max(0, prev - 1));
    };

    const handleNext = () => {
        setCarouselIndex((prev: number) => Math.min(maxIndex, prev + 1));
    };

    const handleSwitchCompImage = (imageUrl: string): void => {
        if (imageUrl === currentImage) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setCurrentImage(imageUrl);
    };

    const handleSetCompImage = (imageUrl: string): void => {
        if (!clickedCompImage) return;

        // If a custom override was active, clear it so CA selection is the source of truth again
        if (isCustomOverrideActive) {
            formMethods.setValue(prosConsKey, []);
            formMethods.clearErrors(prosConsKey);
        }

        setSelectedCompImageUrl(imageUrl);

        if (imageUrl === currentImage) {
            setIsLoading(false);
        }
        else {
            setIsLoading(true);
            setCurrentImage(imageUrl);
        }

        changeCompImage(clickedCompImage, imageUrl);
    };

    return (
        <Modal
            isOpen={!!clickedCompImage || !!imageListOverride}
            onClose={() => {
                setClickSelectableComp(null);
                onClose();
            }}
            title={titleOverride ?? clickedCompImage?.address ?? 'Placeholder image'}
            maxWidth="2xl"
        >
            {currentImage && (
                <>
                    <div className="relative" style={{ maxHeight: '37vh' }}>
                        {/* Current Comp Image */}
                        <img
                            src={currentImage ?? undefined}
                            alt={currentImage ?? 'Placeholder image'}
                            className={`w-full max-w-full h-auto max-h-[37vh] object-contain rounded-2xl ${isLoading ? "opacity-50" : ""}`}
                            onLoad={() => setIsLoading(false)}
                            onError={() => setIsLoading(false)}
                        />
                        {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Loader className="animate-spin h-20 w-20 text-gray-300"/>
                            </div>
                        )}
                    </div>
                    {/* More CA Images */}
                    <div className="flex flex-row items-center gap-2">
                        {/* Left Arrow */}
                        <div className="flex-shrink-0 w-7">
                            {carouselIndex > 0 ? (
                                <button
                                    onClick={handlePrevious}
                                    className="p-1 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                                >
                                    <ChevronLeft className="h-5 w-5" />
                                </button>
                            ) : (
                                <div className="w-7 h-7" />
                            )}
                        </div>

                        {/* Images Container */}
                        <div className="grid grid-cols-3 grid-rows-2 gap-2 flex-1 mt-2 max-h-fit">
                            {imageList && Array.isArray(imageList) && imageList.length > 1 && imageList
                                .slice(carouselIndex, carouselIndex + itemsPerPage)
                                .map((imageUrl, index) => (
                                    <div key={`${imageUrl}-${carouselIndex + index}`}>
                                        <div className="relative w-full">
                                            <img
                                                src={imageUrl ?? undefined}
                                                alt={imageUrl ? `Property at ${imageUrl}` : 'Placeholder image'}
                                                className="w-full max-w-full object-contain rounded-2xl max-h-32 cursor-pointer"
                                                onClick={() => handleSwitchCompImage(imageUrl)}
                                            />
                                            {!isLoading && clickedCompImage &&
                                                <button
                                                    className="absolute top-0.5 right-2 bg-black/10 hover:bg-black/20 p-1 rounded-full transition-colors"
                                                    onClick={(e) => {
                                                        handleSetCompImage(imageUrl);
                                                        e.preventDefault();
                                                    }}
                                                >
                                                    {isSelectedForComp(imageUrl) ? (
                                                        <div style={{
                                                            width: '28px',
                                                            height: '28px',
                                                            borderRadius: '50%',
                                                            background: '#10b981',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <Check size={18} color="white" strokeWidth={3} />
                                                        </div>
                                                    ) : (
                                                        <Circle size={28} color="white" strokeWidth={2} />
                                                    )}
                                                </button>
                                            }
                                        </div>
                                    </div>
                                ))}
                        </div>

                        {/* Right Arrow */}
                        {carouselIndex < maxIndex && (
                            <button
                                onClick={handleNext}
                                className="flex-shrink-0 p-1 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                </>
            )}
        </Modal>
    );
}

export const CompCaImageModal = React.memo(CompCaImageModalRenderer);